import { PERMISSION_TYPES, type PermissionType } from "@/lib/constants/permissions";

/** Workflow-oriented groups for UI only; backend permission `type` stays flat. */
export const PERMISSION_GROUP_IDS = [
	"overview",
	"sales_pos",
	"catalog",
	"procurement",
	"finance",
	"reports",
	"organization",
	"administration",
	"system",
	"other",
] as const;

export type PermissionGroupId = (typeof PERMISSION_GROUP_IDS)[number];

const TYPE_TO_GROUP: Record<string, PermissionGroupId> = {
	dashboard: "overview",
	sales: "sales_pos",
	pos: "sales_pos",
	returns: "sales_pos",
	customers: "sales_pos",
	products: "catalog",
	categories: "catalog",
	recipes: "catalog",
	inventory: "catalog",
	suppliers: "procurement",
	purchases: "procurement",
	expenses: "finance",
	taxes: "finance",
	paymentMethods: "finance",
	cashierCatalog: "sales_pos",
	cashierSession: "sales_pos",
	cashierDrawer: "sales_pos",
	cashierHold: "sales_pos",
	cashierCustomer: "sales_pos",
	cashierDiscount: "sales_pos",
	cashierReturn: "sales_pos",
	cashierCoupon: "sales_pos",
	cashierStoreCredit: "sales_pos",
	cashierGiftCard: "sales_pos",
	cashierLoyalty: "sales_pos",
	cashierSplitBill: "sales_pos",
	cashierReceipt: "sales_pos",
	reports: "reports",
	branches: "organization",
	departments: "organization",
	company: "organization",
	users: "administration",
	roles: "administration",
	settings: "system",
	notifications: "system",
	audit: "administration",
	storefront: "system",
	stockTransfers: "catalog",
	stocktransfers: "catalog",
};

const ACTION_ORDER: Record<string, number> = {
	read: 0,
	create: 1,
	update: 2,
	delete: 3,
	export: 4,
	approve: 5,
	receive: 6,
};

export function parsePermissionName(name: string): { module: string; action: string } {
	const idx = name.indexOf(":");
	if (idx === -1) return { module: name, action: "" };
	return { module: name.slice(0, idx), action: name.slice(idx + 1) };
}

export function getWorkflowGroupForPermissionType(type: string | undefined): PermissionGroupId {
	if (!type) return "other";
	return TYPE_TO_GROUP[type] ?? "other";
}

export function getPermissionModuleKey(permission: { type?: string; name: string }): string {
	if (permission.type) return permission.type;
	return parsePermissionName(permission.name).module;
}

export function getPermissionActionKey(permission: { action?: string; name: string }): string {
	if (permission.action) return permission.action;
	return parsePermissionName(permission.name).action;
}

function moduleOrderIndex(moduleKey: string): number {
	const i = PERMISSION_TYPES.indexOf(moduleKey as PermissionType);
	return i === -1 ? 999 : i;
}

export function sortPermissionsForDisplay<T extends { name: string; type?: string; action?: string }>(list: T[]): T[] {
	return [...list].sort((a, b) => {
		const ma = getPermissionModuleKey(a);
		const mb = getPermissionModuleKey(b);
		const mc = moduleOrderIndex(ma) - moduleOrderIndex(mb);
		if (mc !== 0) return mc;
		const aa = getPermissionActionKey(a);
		const ab = getPermissionActionKey(b);
		return (ACTION_ORDER[aa] ?? 99) - (ACTION_ORDER[ab] ?? 99);
	});
}

export type PermissionUiGroup<T> = {
	groupId: PermissionGroupId;
	modules: { moduleKey: string; items: T[] }[];
};

export function buildPermissionUiGroups<T extends { name: string; type?: string; action?: string }>(
	permissions: T[],
): PermissionUiGroup<T>[] {
	const sorted = sortPermissionsForDisplay(permissions);
	const tree = new Map<PermissionGroupId, Map<string, T[]>>();

	for (const p of sorted) {
		const moduleKey = getPermissionModuleKey(p);
		const groupId = getWorkflowGroupForPermissionType(moduleKey);
		if (!tree.has(groupId)) tree.set(groupId, new Map());
		const modules = tree.get(groupId)!;
		if (!modules.has(moduleKey)) modules.set(moduleKey, []);
		modules.get(moduleKey)!.push(p);
	}

	const result: PermissionUiGroup<T>[] = [];
	for (const groupId of PERMISSION_GROUP_IDS) {
		const modules = tree.get(groupId);
		if (!modules?.size) continue;
		const orderedModules = Array.from(modules.entries()).sort(
			([a], [b]) => moduleOrderIndex(a) - moduleOrderIndex(b),
		);
		result.push({
			groupId,
			modules: orderedModules.map(([moduleKey, items]) => ({ moduleKey, items })),
		});
	}
	return result;
}
